import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { Artist, Release } from '../../types';
import { TrackDetailView } from '../../components/shared';

const ArtistDetail: React.FC = () => {
  const { artistId } = useParams<{ artistId: string }>();
  const navigate = useNavigate();
  const { artists, releases, getArtistById } = useData();
  
  const [artist, setArtist] = useState<Artist | null>(null);
  const [artistReleases, setArtistReleases] = useState<Release[]>([]);
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);

  useEffect(() => {
    if (artistId) {
      const foundArtist = getArtistById(artistId);
      setArtist(foundArtist || null);
      
      // Get all releases for this artist (regardless of status)
      const artistReleasesData = releases.filter(release => release.artist_id === artistId);
      setArtistReleases(artistReleasesData);
    }
  }, [artistId, artists, releases, getArtistById]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'rejected':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'draft':
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
      case 'submitted':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'published':
        return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Onaylandı';
      case 'pending':
        return 'Beklemede';
      case 'rejected':
        return 'Reddedildi';
      case 'draft':
        return 'Taslak';
      case 'submitted':
        return 'Platforma İletildi';
      case 'published':
        return 'Yayınlandı';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!artist) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="glass-card text-center py-12">
          <svg className="mx-auto h-12 w-12 text-white/40 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <h3 className="text-xl font-bold text-white mb-2">Sanatçı Bulunamadı</h3>
          <p className="text-white/70 mb-6">Aradığınız sanatçı mevcut değil veya silinmiş olabilir.</p>
          <button
            onClick={() => navigate('/dashboard/admin/artists')}
            className="btn-primary"
          >
            Sanatçı Listesine Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header with back button */}
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => navigate('/dashboard/admin/artists')}
          className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors duration-200"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>Sanatçı Listesi</span>
        </button>
      </div>

      {/* Artist Info Card */}
      <div className="glass-card">
        <div className="flex items-start space-x-6">
          <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-2xl">
              {artist.display_name[0]?.toUpperCase()}
            </span>
          </div>
          
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-white mb-2">{artist.display_name}</h1>
            <p className="text-xl text-white/80 mb-4">{artist.first_name} {artist.last_name}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <span className="text-sm text-white/60">Email:</span>
                <p className="text-white">{artist.email}</p>
              </div>
              <div>
                <span className="text-sm text-white/60">Kayıt Tarihi:</span>
                <p className="text-white">{formatDate(artist.created_at)}</p>
              </div>
              <div>
                <span className="text-sm text-white/60">Durum:</span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30 ml-2">
                  Aktif
                </span>
              </div>
              <div>
                <span className="text-sm text-white/60">Toplam Şarkı:</span>
                <p className="text-white font-semibold">{artistReleases.length}</p>
              </div>
            </div>

            {artist.bio && (
              <div className="mb-4">
                <span className="text-sm text-white/60">Biyografi:</span>
                <p className="text-white/80 mt-1">{artist.bio}</p>
              </div>
            )}

            {/* Platform Hesap Bilgileri */}
            {artist.is_artist_exist_on_platforms && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <span className="text-sm text-white/60 mb-3 block">Platform Hesapları:</span>
                <div className="flex flex-wrap gap-3">
                  {artist.spotify_artist_profile_url && (
                    <a
                      href={artist.spotify_artist_profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-green-600/20 hover:bg-green-600/30 text-green-300 border border-green-600/30 rounded-lg transition-colors duration-200"
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                      </svg>
                      Spotify Profile
                    </a>
                  )}
                  {artist.apple_music_artist_profile_url && (
                    <a
                      href={artist.apple_music_artist_profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-pink-600/20 hover:bg-pink-600/30 text-pink-300 border border-pink-600/30 rounded-lg transition-colors duration-200"
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.132c-.317-1.314-1.062-2.31-2.17-3.025a5.334 5.334 0 00-2.186-.681 39.246 39.246 0 00-2.956-.1h-7.64c-.92 0-1.841.024-2.759.1a5.4 5.4 0 00-2.186.681c-1.108.715-1.853 1.711-2.17 3.025a9.123 9.123 0 00-.24 2.132c-.024.776-.035 1.552-.035 2.328v7.096c0 .776.011 1.552.035 2.328.024.776.086 1.454.24 2.132.317 1.314 1.062 2.31 2.17 3.025a5.334 5.334 0 002.186.681c.92.076 1.839.1 2.759.1h7.64c.92 0 1.841-.024 2.759-.1a5.334 5.334 0 002.186-.681c1.108-.715 1.853-1.711 2.17-3.025.154-.678.216-1.356.24-2.132.024-.776.035-1.552.035-2.328V8.452c0-.776-.011-1.552-.035-2.328zm-4.165 6.155v.186a4.337 4.337 0 01-.748 2.549 3.511 3.511 0 01-2.757 1.48c-.895 0-1.648-.396-2.181-1.162-.591-.852-.895-1.915-.895-3.18V5.986h1.972v6.197c0 .936.155 1.647.437 2.139.296.484.724.739 1.307.739.486 0 .916-.196 1.241-.599.3-.36.451-.791.451-1.316v-7.16h1.972v4.943l.201-.014z"/>
                      </svg>
                      Apple Music Profile
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Releases Section */}
      <div className="glass-card">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
          <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
          </svg>
          Tüm Şarkıları ({artistReleases.length})
        </h2>

        {artistReleases.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-white/40 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
            <h3 className="text-xl font-bold text-white mb-2">Henüz Şarkı Yok</h3>
            <p className="text-white/70">Bu sanatçı henüz hiç şarkı yüklememiş.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {artistReleases.map((release) => (
              <div
                key={release.id}
                className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all duration-200 cursor-pointer"
                onClick={() => setSelectedRelease(release)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-white mb-1">{release.title}</h3>
                    <p className="text-sm text-white/60 mb-2">{release.artist_display_name}</p>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(release.status)}`}>
                    {getStatusText(release.status)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-white/60">Tür:</span>
                    <p className="text-white">{release.genre}</p>
                  </div>
                  <div>
                    <span className="text-white/60">Yayın Tarihi:</span>
                    <p className="text-white">{formatDate(release.release_date)}</p>
                  </div>
                  <div>
                    <span className="text-white/60">Tip:</span>
                    <p className="text-white">{release.type}</p>
                  </div>
                  <div>
                    <span className="text-white/60">Şarkı Sayısı:</span>
                    <p className="text-white">{release.tracks?.length || 0}</p>
                  </div>
                </div>

                {/* ISRC/UPC Info */}
                {release.rights && (release.rights.isrc || release.rights.upc) && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="flex flex-wrap gap-2 text-xs">
                      {release.rights.isrc && (
                        <span className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded">
                          ISRC: {release.rights.isrc}
                        </span>
                      )}
                      {release.rights.upc && (
                        <span className="bg-green-500/20 text-green-300 px-2 py-1 rounded">
                          UPC: {release.rights.upc}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-white/50">
                    Oluşturulma: {formatDate(release.created_at)}
                  </span>
                  <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Track Detail Modal */}
      {selectedRelease && (
        <TrackDetailView
          release={selectedRelease}
          onClose={() => setSelectedRelease(null)}
        />
      )}
    </div>
  );
};

export default ArtistDetail;